import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { getLoginUrl } from "@/const";
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Inbox,
  Bell,
  Plus,
  Eye,
  Trash2,
  ToggleLeft,
  ToggleRight,
  ArrowLeft,
  MessageSquare,
  Clock,
  Mail,
  Phone,
  Building2,
  Paperclip,
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
  LogOut,
  Shield,
  BarChart3,
  TrendingUp,
  Globe,
  Target,
  Link2,
  ExternalLink,
} from "lucide-react";

type Tab = "submissions" | "banners" | "utm";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>("submissions");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="p-8 max-w-md w-full text-center">
          <Shield className="w-12 h-12 text-[#2563EB] mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold text-gray-900 mb-2">
            Admin-inloggning krävs
          </h1>
          <p className="text-gray-500 mb-6">
            Du måste vara inloggad som administratör för att komma åt denna sida.
          </p>
          <Button
            onClick={() => (window.location.href = getLoginUrl())}
            className="bg-[#2563EB] hover:bg-[#1D4ED8]"
          >
            Logga in
          </Button>
        </Card>
      </div>
    );
  }

  if (user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="p-8 max-w-md w-full text-center">
          <Shield className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold text-gray-900 mb-2">
            Åtkomst nekad
          </h1>
          <p className="text-gray-500 mb-6">
            Du har inte behörighet att komma åt admin-panelen.
          </p>
          <Button variant="outline" onClick={() => (window.location.href = "/")}>
            Tillbaka till startsidan
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <a
                href="/"
                className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Tillbaka till sajten</span>
              </a>
              <div className="h-6 w-px bg-gray-200" />
              <h1 className="font-heading text-lg font-bold text-gray-900">
                ASPAB Admin
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <UnreadBadge />
              <span className="text-sm text-gray-500">{user.name}</span>
              <Button variant="outline" size="sm" onClick={logout}>
                <LogOut className="w-4 h-4 mr-1" />
                Logga ut
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-6">
            <button
              onClick={() => setActiveTab("submissions")}
              className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "submissions"
                  ? "border-[#2563EB] text-[#2563EB]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <Inbox className="w-4 h-4 inline mr-1.5" />
              Kontaktförfrågningar
            </button>
            <button
              onClick={() => setActiveTab("banners")}
              className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "banners"
                  ? "border-[#2563EB] text-[#2563EB]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <Bell className="w-4 h-4 inline mr-1.5" />
              Meddelanden till besökare
            </button>
            <button
              onClick={() => setActiveTab("utm")}
              className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "utm"
                  ? "border-[#2563EB] text-[#2563EB]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-1.5" />
              UTM-analys
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === "submissions" ? (
          <SubmissionsPanel />
        ) : activeTab === "banners" ? (
          <BannersPanel />
        ) : (
          <UtmPanel />
        )}
      </div>
    </div>
  );
}

// ============================================================
// Unread Badge
// ============================================================
function UnreadBadge() {
  const { data } = trpc.admin.submissions.unreadCount.useQuery(undefined, {
    refetchInterval: 30_000,
  });

  if (!data || data.count === 0) return null;

  return (
    <Badge className="bg-red-500 text-white text-xs px-2 py-0.5">
      {data.count} nya
    </Badge>
  );
}

// ============================================================
// Submissions Panel
// ============================================================
function SubmissionsPanel() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const pageSize = 15;

  const { data, isLoading, refetch } = trpc.admin.submissions.list.useQuery(
    { page, pageSize, status: statusFilter as any },
    { refetchInterval: 30_000 }
  );

  if (selectedId !== null) {
    return (
      <SubmissionDetail
        id={selectedId}
        onBack={() => {
          setSelectedId(null);
          refetch();
        }}
      />
    );
  }

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  return (
    <div>
      {/* Filters */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          {[
            { value: undefined, label: "Alla" },
            { value: "new", label: "Nya" },
            { value: "in_progress", label: "Pågående" },
            { value: "replied", label: "Besvarade" },
            { value: "archived", label: "Arkiverade" },
          ].map((filter) => (
            <Button
              key={filter.value ?? "all"}
              variant={statusFilter === filter.value ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setStatusFilter(filter.value);
                setPage(1);
              }}
              className={
                statusFilter === filter.value
                  ? "bg-[#2563EB] hover:bg-[#1D4ED8]"
                  : ""
              }
            >
              {filter.label}
            </Button>
          ))}
        </div>
        <span className="text-sm text-gray-500">
          {data?.total ?? 0} förfrågningar totalt
        </span>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
        </div>
      ) : data?.items.length === 0 ? (
        <Card className="p-12 text-center">
          <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Inga kontaktförfrågningar ännu.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {data?.items.map((item) => (
            <Card
              key={item.id}
              className={`p-4 cursor-pointer hover:shadow-md transition-shadow ${
                !item.isRead ? "border-l-4 border-l-[#2563EB] bg-blue-50/30" : ""
              }`}
              onClick={() => setSelectedId(item.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-sm ${
                          !item.isRead ? "font-semibold text-gray-900" : "text-gray-700"
                        }`}
                      >
                        {item.name}
                      </span>
                      {item.company && (
                        <span className="text-xs text-gray-500">
                          ({item.company})
                        </span>
                      )}
                      <StatusBadge status={item.status} />
                    </div>
                    <p className="text-sm text-gray-500 truncate mt-0.5 max-w-lg">
                      {item.message}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {item.attachments && JSON.parse(item.attachments).length > 0 && (
                    <Paperclip className="w-4 h-4 text-gray-500" />
                  )}
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {new Date(item.createdAt).toLocaleDateString("sv-SE")}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-gray-500">
            Sida {page} av {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Submission Detail
// ============================================================
function SubmissionDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const { data: submission, isLoading } =
    trpc.admin.submissions.getById.useQuery({ id });
  const markAsRead = trpc.admin.submissions.markAsRead.useMutation();
  const updateSubmission = trpc.admin.submissions.update.useMutation();
  const utils = trpc.useUtils();
  const [notes, setNotes] = useState("");

  // Auto-mark as read
  useMemo(() => {
    if (submission && !submission.isRead) {
      markAsRead.mutate(
        { id },
        {
          onSuccess: () => {
            utils.admin.submissions.unreadCount.invalidate();
          },
        }
      );
    }
  }, [submission?.id]);

  useMemo(() => {
    if (submission?.adminNotes) {
      setNotes(submission.adminNotes);
    }
  }, [submission?.adminNotes]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (!submission) {
    return (
      <Card className="p-8 text-center">
        <p className="text-gray-500">Förfrågan hittades inte.</p>
        <Button variant="outline" onClick={onBack} className="mt-4">
          Tillbaka
        </Button>
      </Card>
    );
  }

  const attachments = submission.attachments
    ? JSON.parse(submission.attachments)
    : [];

  const handleStatusChange = (status: string) => {
    updateSubmission.mutate(
      { id, status: status as any },
      {
        onSuccess: () => {
          utils.admin.submissions.getById.invalidate({ id });
          utils.admin.submissions.list.invalidate();
        },
      }
    );
  };

  const handleSaveNotes = () => {
    updateSubmission.mutate(
      { id, adminNotes: notes },
      {
        onSuccess: () => {
          utils.admin.submissions.getById.invalidate({ id });
        },
      }
    );
  };

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Tillbaka till listan
      </button>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-gray-900">
                {submission.name}
              </h2>
              <StatusBadge status={submission.status} />
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail className="w-4 h-4 text-gray-500" />
                <a
                  href={`mailto:${submission.email}`}
                  className="text-[#2563EB] hover:underline"
                >
                  {submission.email}
                </a>
              </div>
              {submission.phone && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <a
                    href={`tel:${submission.phone}`}
                    className="text-[#2563EB] hover:underline"
                  >
                    {submission.phone}
                  </a>
                </div>
              )}
              {submission.company && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Building2 className="w-4 h-4 text-gray-500" />
                  {submission.company}
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-4 h-4 text-gray-500" />
                {new Date(submission.createdAt).toLocaleString("sv-SE")}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                <MessageSquare className="w-4 h-4" />
                Meddelande
              </h3>
              <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                {submission.message}
              </p>
            </div>

            {attachments.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                  <Paperclip className="w-4 h-4" />
                  Bifogade filer ({attachments.length})
                </h3>
                <div className="space-y-2">
                  {attachments.map((file: any, i: number) => (
                    <a
                      key={i}
                      href={file.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <FileText className="w-4 h-4 text-[#2563EB]" />
                      <span className="text-sm text-gray-800">
                        {file.filename}
                      </span>
                      <span className="text-xs text-gray-500 ml-auto">
                        {formatFileSize(file.size)}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Admin Notes */}
          <Card className="p-6">
            <h3 className="font-heading text-sm font-semibold text-gray-700 mb-3">
              Interna anteckningar
            </h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Skriv interna anteckningar om denna förfrågan..."
              className="w-full h-32 p-3 border border-gray-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent outline-none"
            />
            <div className="flex justify-end mt-2">
              <Button
                size="sm"
                onClick={handleSaveNotes}
                disabled={updateSubmission.isPending}
                className="bg-[#2563EB] hover:bg-[#1D4ED8]"
              >
                {updateSubmission.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : null}
                Spara anteckningar
              </Button>
            </div>
          </Card>
        </div>

        {/* Sidebar actions */}
        <div className="space-y-4">
          <Card className="p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Ändra status
            </h3>
            <div className="space-y-2">
              {[
                { value: "new", label: "Ny", color: "bg-blue-100 text-blue-800" },
                {
                  value: "in_progress",
                  label: "Pågående",
                  color: "bg-yellow-100 text-yellow-800",
                },
                {
                  value: "replied",
                  label: "Besvarad",
                  color: "bg-green-100 text-green-800",
                },
                {
                  value: "archived",
                  label: "Arkiverad",
                  color: "bg-gray-100 text-gray-600",
                },
              ].map((s) => (
                <button
                  key={s.value}
                  onClick={() => handleStatusChange(s.value)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    submission.status === s.value
                      ? s.color + " font-medium"
                      : "hover:bg-gray-50 text-gray-600"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </Card>

          <Card className="p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Snabbåtgärder
            </h3>
            <div className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() =>
                  window.open(`mailto:${submission.email}?subject=Re: Offertförfrågan via aspab.com`)
                }
              >
                <Mail className="w-4 h-4 mr-2" />
                Svara via e-post
              </Button>
              {submission.phone && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => window.open(`tel:${submission.phone}`)}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Ring {submission.phone}
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Banners Panel
// ============================================================
function BannersPanel() {
  const { data: banners, isLoading, refetch } =
    trpc.admin.banners.list.useQuery({});
  const createBanner = trpc.admin.banners.create.useMutation();
  const toggleBanner = trpc.admin.banners.toggleActive.useMutation();
  const deleteBanner = trpc.admin.banners.delete.useMutation();
  const utils = trpc.useUtils();
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [type, setType] = useState<"info" | "warning" | "success" | "promotion">("info");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [dismissible, setDismissible] = useState(true);

  const handleCreate = () => {
    createBanner.mutate(
      {
        title,
        message,
        type,
        linkUrl: linkUrl || undefined,
        linkText: linkText || undefined,
        dismissible,
      },
      {
        onSuccess: () => {
          setShowForm(false);
          setTitle("");
          setMessage("");
          setType("info");
          setLinkUrl("");
          setLinkText("");
          setDismissible(true);
          utils.admin.banners.list.invalidate();
          utils.banners.getActive.invalidate();
        },
      }
    );
  };

  const handleToggle = (id: number, currentActive: boolean) => {
    toggleBanner.mutate(
      { id, isActive: !currentActive },
      {
        onSuccess: () => {
          utils.admin.banners.list.invalidate();
          utils.banners.getActive.invalidate();
        },
      }
    );
  };

  const handleDelete = (id: number) => {
    if (!confirm("Vill du verkligen ta bort denna banner?")) return;
    deleteBanner.mutate(
      { id },
      {
        onSuccess: () => {
          utils.admin.banners.list.invalidate();
          utils.banners.getActive.invalidate();
        },
      }
    );
  };

  const typeLabels: Record<string, string> = {
    info: "Information",
    warning: "Varning",
    success: "Bekräftelse",
    promotion: "Kampanj",
  };

  const typeColors: Record<string, string> = {
    info: "bg-blue-100 text-blue-800",
    warning: "bg-amber-100 text-amber-800",
    success: "bg-emerald-100 text-emerald-800",
    promotion: "bg-purple-100 text-purple-800",
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-lg font-bold text-gray-900">
            Meddelanden till besökare
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Skapa banners som visas för alla besökare på webbplatsen.
          </p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-[#2563EB] hover:bg-[#1D4ED8]"
        >
          <Plus className="w-4 h-4 mr-1" />
          Ny banner
        </Button>
      </div>

      {/* Create Form */}
      {showForm && (
        <Card className="p-6 mb-6">
          <h3 className="font-heading text-sm font-semibold text-gray-700 mb-4">
            Skapa ny banner
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rubrik
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="T.ex. Sommarstängt"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2563EB] focus:border-transparent outline-none"
                maxLength={200}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meddelande
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="T.ex. Vi har stängt vecka 28-29. Kontakta oss via e-post."
                className="w-full h-20 px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent outline-none"
                maxLength={2000}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Typ
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2563EB] focus:border-transparent outline-none bg-white"
                >
                  <option value="info">Information (blå)</option>
                  <option value="warning">Varning (gul)</option>
                  <option value="success">Bekräftelse (grön)</option>
                  <option value="promotion">Kampanj (lila gradient)</option>
                </select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="dismissible"
                  checked={dismissible}
                  onChange={(e) => setDismissible(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <label htmlFor="dismissible" className="text-sm text-gray-700">
                  Besökare kan stänga bannern
                </label>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Länk-URL (valfritt)
                </label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2563EB] focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Länktext (valfritt)
                </label>
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder="T.ex. Läs mer"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2563EB] focus:border-transparent outline-none"
                  maxLength={100}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowForm(false)}
              >
                Avbryt
              </Button>
              <Button
                size="sm"
                onClick={handleCreate}
                disabled={!title || !message || createBanner.isPending}
                className="bg-[#2563EB] hover:bg-[#1D4ED8]"
              >
                {createBanner.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : null}
                Skapa banner
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Banner List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
        </div>
      ) : banners?.length === 0 ? (
        <Card className="p-12 text-center">
          <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Inga banners skapade ännu.</p>
          <p className="text-sm text-gray-500 mt-1">
            Klicka "Ny banner" för att skapa en.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {banners?.map((banner) => (
            <Card key={banner.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      banner.isActive ? "bg-green-500" : "bg-gray-300"
                    }`}
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {banner.title}
                      </span>
                      <Badge
                        className={`text-xs ${
                          typeColors[banner.type] || typeColors.info
                        }`}
                      >
                        {typeLabels[banner.type] || banner.type}
                      </Badge>
                      {!banner.isActive && (
                        <Badge variant="outline" className="text-xs">
                          Inaktiv
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate mt-0.5">
                      {banner.message}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggle(banner.id, banner.isActive)}
                    title={banner.isActive ? "Inaktivera" : "Aktivera"}
                  >
                    {banner.isActive ? (
                      <ToggleRight className="w-4 h-4 text-green-600" />
                    ) : (
                      <ToggleLeft className="w-4 h-4 text-gray-500" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(banner.id)}
                    className="text-red-500 hover:text-red-700 hover:border-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// UTM Analytics Panel
// ============================================================
function UtmPanel() {
  const { data, isLoading } = trpc.admin.utm.overview.useQuery(undefined, {
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <Card className="p-12 text-center">
        <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">Kunde inte ladda UTM-data.</p>
      </Card>
    );
  }

  const utmPercentage =
    data.totalSubmissions > 0
      ? Math.round((data.withUtm / data.totalSubmissions) * 100)
      : 0;

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading text-lg font-bold text-gray-900">
          UTM-kampanjanalys
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Se vilka kampanjkällor som genererar flest leads via kontaktformuläret.
        </p>
      </div>

      {/* Overview Cards */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <Card className="p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Inbox className="w-5 h-5 text-[#2563EB]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {data.totalSubmissions}
              </p>
              <p className="text-xs text-gray-500">Totalt leads</p>
            </div>
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{data.withUtm}</p>
              <p className="text-xs text-gray-500">
                Med UTM-spårning ({utmPercentage}%)
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center">
              <Globe className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {data.withoutUtm}
              </p>
              <p className="text-xs text-gray-500">Utan UTM (organisk/direkt)</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Top Sources */}
        <Card className="p-5">
          <h3 className="font-heading text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Toppkällor (source / medium / campaign)
          </h3>
          {data.topSources.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">
              Inga UTM-spårade leads ännu. Lägg till utm_source, utm_medium och
              utm_campaign i dina kampanjlänkar.
            </p>
          ) : (
            <div className="space-y-3">
              {data.topSources.map((source, i) => {
                const maxCount = data.topSources[0]?.count ?? 1;
                const barWidth = Math.max(
                  (source.count / maxCount) * 100,
                  8
                );
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {source.utmSource || "(tom)"}
                        </span>
                        {source.utmMedium && (
                          <Badge
                            variant="outline"
                            className="text-xs shrink-0"
                          >
                            {source.utmMedium}
                          </Badge>
                        )}
                        {source.utmCampaign && (
                          <span className="text-xs text-gray-500 truncate">
                            {source.utmCampaign}
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-bold text-[#2563EB] shrink-0 ml-2">
                        {source.count}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className="bg-[#2563EB] h-1.5 rounded-full transition-all"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Top Campaigns + Mediums */}
        <div className="space-y-6">
          <Card className="p-5">
            <h3 className="font-heading text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Target className="w-4 h-4" />
              Toppkampanjer
            </h3>
            {data.topCampaigns.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">
                Inga kampanjer registrerade ännu.
              </p>
            ) : (
              <div className="space-y-2">
                {data.topCampaigns.map((c, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-1.5"
                  >
                    <span className="text-sm text-gray-800">{c.campaign}</span>
                    <Badge className="bg-blue-50 text-[#2563EB] text-xs">
                      {c.count} leads
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-5">
            <h3 className="font-heading text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Link2 className="w-4 h-4" />
              Mediums
            </h3>
            {data.topMediums.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">
                Inga mediums registrerade ännu.
              </p>
            ) : (
              <div className="space-y-2">
                {data.topMediums.map((m, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-1.5"
                  >
                    <span className="text-sm text-gray-800">{m.medium}</span>
                    <Badge className="bg-gray-100 text-gray-700 text-xs">
                      {m.count} leads
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Recent UTM Submissions */}
      <Card className="p-5">
        <h3 className="font-heading text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4" />
          Senaste UTM-spårade leads
        </h3>
        {data.recentUtmSubmissions.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">
            Inga UTM-spårade leads ännu.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">
                    Namn
                  </th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">
                    Källa
                  </th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">
                    Medium
                  </th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">
                    Kampanj
                  </th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">
                    Landningssida
                  </th>
                  <th className="text-left py-2 font-medium text-gray-500">
                    Datum
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.recentUtmSubmissions.map((sub: any) => (
                  <tr
                    key={sub.id}
                    className="border-b border-gray-50 hover:bg-gray-50"
                  >
                    <td className="py-2.5 pr-4">
                      <span className="font-medium text-gray-900">
                        {sub.name}
                      </span>
                      {sub.company && (
                        <span className="text-gray-500 ml-1 text-xs">
                          ({sub.company})
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4">
                      <Badge className="bg-blue-50 text-[#2563EB] text-xs">
                        {sub.utmSource}
                      </Badge>
                    </td>
                    <td className="py-2.5 pr-4 text-gray-600">
                      {sub.utmMedium || "–"}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-600">
                      {sub.utmCampaign || "–"}
                    </td>
                    <td className="py-2.5 pr-4">
                      {sub.landingPage ? (
                        <span className="text-xs text-gray-500 font-mono">
                          {sub.landingPage}
                        </span>
                      ) : (
                        "–"
                      )}
                    </td>
                    <td className="py-2.5 text-gray-500 whitespace-nowrap">
                      {new Date(sub.createdAt).toLocaleDateString("sv-SE")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* UTM Help */}
      <Card className="p-5 mt-6 bg-gray-50 border-dashed">
        <h3 className="font-heading text-sm font-semibold text-gray-700 mb-2">
          Så använder du UTM-spårning
        </h3>
        <p className="text-sm text-gray-500 mb-3">
          Lägg till UTM-parametrar i dina kampanjlänkar för att spåra vilka källor
          som genererar leads:
        </p>
        <div className="bg-white rounded-lg p-3 border border-gray-200 font-mono text-xs text-gray-700 overflow-x-auto">
          https://aspab.com/kontakt?utm_source=<span className="text-[#2563EB]">facebook</span>&utm_medium=<span className="text-green-600">cpc</span>&utm_campaign=<span className="text-purple-600">laser-kampanj-q2</span>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 mt-3">
          <div className="text-xs">
            <span className="font-medium text-gray-700">utm_source</span>
            <span className="text-gray-500 ml-1">– Var kommer trafiken ifrån? (facebook, google, linkedin)</span>
          </div>
          <div className="text-xs">
            <span className="font-medium text-gray-700">utm_medium</span>
            <span className="text-gray-500 ml-1">– Vilken typ av trafik? (cpc, email, social)</span>
          </div>
          <div className="text-xs">
            <span className="font-medium text-gray-700">utm_campaign</span>
            <span className="text-gray-500 ml-1">– Vilken kampanj? (laser-kampanj-q2, nyhetsbrev-april)</span>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================
function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string }> = {
    new: { label: "Ny", className: "bg-blue-100 text-blue-800" },
    in_progress: {
      label: "Pågående",
      className: "bg-yellow-100 text-yellow-800",
    },
    replied: { label: "Besvarad", className: "bg-green-100 text-green-800" },
    archived: { label: "Arkiverad", className: "bg-gray-100 text-gray-600" },
  };

  const c = config[status] || config.new;

  return <Badge className={`text-xs ${c.className}`}>{c.label}</Badge>;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
